import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import {
  useAsyncBrands,
  useAsyncModelsByBrand,
  useAsyncYearsByModelBrand
} from "@/hooks/Vehicles/Enums";
import { useIsMount } from "@/utils/useIsMount";
import { Button, Flex, useToast, VStack } from "@chakra-ui/react";
import { Select2, Select2OptionProps } from "../Select2";

type VehicleSearchProps = {
  visible: boolean;
  handleMenu?: () => void;
  OnCloseDrawer?: () => void;
};

export function VehicleSearchMenu({ visible, handleMenu, OnCloseDrawer }: VehicleSearchProps) {
  // ! Env Data
  const DefaultBrand = "TOYOTA";
  const DefaultModel = "HILUX";
  const DefaultYear = "2020";
  // ! Initial States
  const [Brands, setBrands] = useState<Select2OptionProps[]>([]);
  const [SelectedBrand, setSelectedBrand] = useState<string>(DefaultBrand);
  const [Models, setModels] = useState<Select2OptionProps[]>([]);
  const [SelectedModel, setSelectedModel] = useState<string>(DefaultModel);
  const [Years, setYears] = useState<Select2OptionProps[]>([]);
  const [SelectedYear, setSelectedYear] = useState<string>(DefaultYear);
  // ! Controllers & Rullers
  const [ModelTrigger, setModelTrigger] = useState<number>(0);
  const [YearTrigger, setYearTrigger] = useState<number>(0);
  const IsFirstMount = useIsMount();
  const Toast = useToast({
    position: "bottom",
    duration: 3000,
    isClosable: true
  });
  const Router = useRouter();
  // ! Methods
  async function requestBrands() {
    const [BData, BError] = await useAsyncBrands();
    if (BData && BError === null) {
      setBrands(
        BData?.map((brand: string) => ({
          label: brand,
          value: brand
        }))
      );
    }
  }
  async function requestModels(Brand: string) {
    const [MData, MError] = await useAsyncModelsByBrand(Brand);
    if (MData && MError === null) {
      setModels(
        MData?.map((model: string) => ({
          label: model,
          value: model
        }))
      );
    }
  }
  async function requestYears(Brand: string, Model: string) {
    const [YData, YError] = await useAsyncYearsByModelBrand(Brand, Model);
    if (YData && YError === null) {
      setYears(
        YData?.map((comp) => ({
          label: comp.year,
          value: comp.year
        }))
      );
    }
  }
  async function submitVehicleSearch(e = null) {
    Toast.closeAll();
    switch (true) {
      case !Boolean(SelectedBrand):
        Toast({
          title: "Selecione uma marca para realizar a pesquisa.",
          status: "info"
        });
        return false;
      case !Boolean(SelectedModel):
        Toast({
          title: "Selecione um modelo para realizar a pesquisa.",
          status: "info"
        });
        return false;
      default:
        Router.push({
          pathname: "/pesquisa/veiculo",
          query: Boolean(e)
            ? {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`,
                year: `${SelectedYear ?? e ?? ""}`
              }
            : {
                brand: `${SelectedBrand}`,
                model: `${SelectedModel}`
              }
        });
    }
  }
  // ! Effects
  useEffect(() => {
    requestBrands();
    requestModels(DefaultBrand);
    requestYears(DefaultBrand, DefaultModel);
  }, []);

  return (
    <Flex direction="column" w="full" bg={"white"} alignItems={"center"}>
      <Flex
        flexDir={"column"}
        gap={"10px"}
        w={"90%"}
        h={"auto"}
        mt={"10px"}
        mb={"10px"}
        borderBottomRadius={"10px"}
      >
        {/* BRAND SELECT */}
        <Select2
          customW="full"
          PopAdjust={"0px"}
          mr="0px"
          OptionsData={Brands}
          setSelected={setSelectedBrand}
          Selected={SelectedBrand}
          HasImg={true}
          Callback={(e: string) => {
            setSelectedModel(null);
            setSelectedYear(null);
            requestModels(e);
            setModelTrigger((prev) => prev + 1);
          }}
        />
        {/* MODEL SELECT */}
        <Select2
          customW="full"
          PopAdjust={"0px"}
          OptionsData={Models}
          setSelected={setSelectedModel}
          Selected={SelectedModel}
          Trigger={ModelTrigger}
          Callback={(e: string) => {
            setSelectedYear(null);
            requestYears(SelectedBrand, e);
            setYearTrigger((prev) => prev + 1);
          }}
        />
        <Select2
          customW="full"
          PopAdjust={"0px"}
          OptionsData={Years}
          setSelected={setSelectedYear}
          Selected={SelectedYear}
          Trigger={YearTrigger}
          HasNullOption={true}
          Callback={(e: string) => {
            setSelectedYear(e);
          }}
        />

        <Button
          variant={"primary-button"}
          type="submit"
          onClick={submitVehicleSearch}
          _hover={{ opacity: "0.5" }}
        >
          BUSCAR
        </Button>
      </Flex>
    </Flex>
  );
}
